import {
  Button,
  KeyReferenceBar,
  KeyReferenceBarSection,
  useToast,
} from '@apex/component-library'
import { useDisclosure } from '@chakra-ui/react'
import React, { useState } from 'react'
import { FaExternalLinkAlt } from 'react-icons/fa'
import { connect, ConnectedProps } from 'react-redux'
import { Dispatch } from 'redux'
import { caseAccept, caseReject } from '../../../../actions'
import { ContextualLink } from '../../../../components/ContextualLink'
import { PermissionsEnum } from '../../../../constants'
import { usePermissions } from '../../../../hooks'
import { RootState } from '../../../../store'
import { getAppConfigSetting } from '../../../../utils'
import { formatDate } from '../../../../utils/dateUtil'
import { NegativeActionModal, PositiveActionModal } from '../ActionModal'

import {
  accountNumberSelector,
  alpsStatusSelector,
  canBeCompletedSelector,
  expectedSettlementDateSelector,
  nsccStatusSelector,
  processingDirectionSelector,
  publicStatusSelector,
  transferDetailsSelector,
  transferTypeSelector,
} from './selectors'

interface TransferDetailsKeyReferenceBarComponentProps extends PropsFromRedux {
  detailsAccountNumber: string | null
}

const TransferDetailsKeyReferenceBarComponent = ({
  alpsStatus,
  expectedSettlementDate,
  nsccStatus,
  processingDirection,
  publicStatus,
  putCaseAccept,
  putCaseReject,
  transferDetails,
  transferType,
  canBeCompleted,
  detailsAccountNumber,
}: TransferDetailsKeyReferenceBarComponentProps) => {
  const { isOpen, onOpen, onClose } = useDisclosure()
  const [isPositiveModal, setIsPositiveModal] = useState(true)
  const toast = useToast()
  const { hasPermission } = usePermissions()
  const canNavigateToAccount = hasPermission(PermissionsEnum.AccountRead)
  const canAccept =
    hasPermission(PermissionsEnum.AlpsRead) &&
    hasPermission(PermissionsEnum.AlpsModify) &&
    hasPermission(PermissionsEnum.AlpsSubmit) &&
    hasPermission(PermissionsEnum.AccountTransferAcceptButtonActive)
  const canReject =
    hasPermission(PermissionsEnum.AlpsRead) &&
    hasPermission(PermissionsEnum.AlpsModify) &&
    hasPermission(PermissionsEnum.AlpsSubmit)
  const canAcceptOrReject = canAccept || canReject

  const openPositiveModal = (isPositive: boolean) => {
    setIsPositiveModal(isPositive)
    onOpen()
  }

  const hasValidAccountNumber =
    detailsAccountNumber && detailsAccountNumber !== 'None'

  if (!transferDetails)
    return (
      <KeyReferenceBar>
        <KeyReferenceBarSection title="Account Number">
          <br />
          {/* this is to create a line of empty space to reduce screen movement on load */}
        </KeyReferenceBarSection>
        <KeyReferenceBarSection title="Direction" />
        <KeyReferenceBarSection title="Type" />
        <KeyReferenceBarSection title="ALPS State" />
        <KeyReferenceBarSection title="NSCC State" />
        <KeyReferenceBarSection title="Status" />
        <KeyReferenceBarSection title="Expected Settlement Date" />
      </KeyReferenceBar>
    )

  return (
    <>
      <KeyReferenceBar
        rightSideContent={
          canBeCompleted && canAcceptOrReject ? (
            <>
              {canReject && (
                <Button
                  variant="negative"
                  size="lg"
                  fontWeight="bold"
                  onClick={() => openPositiveModal(false)}
                >
                  Reject
                </Button>
              )}
              {canAccept && (
                <Button
                  variant="positive"
                  size="lg"
                  fontWeight="bold"
                  onClick={() => openPositiveModal(true)}
                >
                  Accept
                </Button>
              )}
            </>
          ) : undefined
        }
      >
        <KeyReferenceBarSection title="Account Number">
          {detailsAccountNumber &&
            (canNavigateToAccount && hasValidAccountNumber ? (
              <ContextualLink
                href={`${getAppConfigSetting(
                  'appsUrl'
                )}/accounts/#/${detailsAccountNumber}?skipSearch=true`}
                icon={FaExternalLinkAlt}
                isExternal
              >
                {detailsAccountNumber}
              </ContextualLink>
            ) : (
              `${detailsAccountNumber}`
            ))}
        </KeyReferenceBarSection>
        <KeyReferenceBarSection title="Direction">
          {processingDirection}
        </KeyReferenceBarSection>
        <KeyReferenceBarSection title="Type">
          {transferType}
        </KeyReferenceBarSection>
        <KeyReferenceBarSection title="ALPS State">
          {alpsStatus}
        </KeyReferenceBarSection>
        <KeyReferenceBarSection title="NSCC State">
          {nsccStatus}
        </KeyReferenceBarSection>
        <KeyReferenceBarSection title="Status">
          {publicStatus}
        </KeyReferenceBarSection>
        <KeyReferenceBarSection title="Expected Settlement Date">
          {expectedSettlementDate ? formatDate(expectedSettlementDate) : ''}
        </KeyReferenceBarSection>
      </KeyReferenceBar>
      {isPositiveModal ? (
        <PositiveActionModal
          isOpen={isOpen}
          onClose={onClose}
          putCaseAccept={() =>
            putCaseAccept(transferDetails.processingCaseId, toast)
          }
        />
      ) : (
        <NegativeActionModal
          isOpen={isOpen}
          onClose={onClose}
          putCaseReject={({
            comment,
            rejectReason,
          }: {
            comment: string
            rejectReason: string
          }) =>
            putCaseReject(
              transferDetails.processingCaseId,
              rejectReason,
              comment,
              toast
            )
          }
        />
      )}
    </>
  )
}

const mapStateToProps = (state: RootState) => ({
  alpsStatus: alpsStatusSelector(state),
  expectedSettlementDate: expectedSettlementDateSelector(state),
  nsccStatus: nsccStatusSelector(state),
  processingDirection: processingDirectionSelector(state),
  publicStatus: publicStatusSelector(state),
  accountNumber: accountNumberSelector(state),
  transferDetails: transferDetailsSelector(state),
  transferType: transferTypeSelector(state),
  canBeCompleted: canBeCompletedSelector(state),
})

const mapDispatchToProps = (dispatch: Dispatch) => ({
  putCaseAccept: (processingCaseId: number, toast: any) =>
    caseAccept(dispatch, processingCaseId, toast),
  putCaseReject: (
    processingCaseId: number,
    reason: string,
    comment: string,
    toast: any
  ) => caseReject(dispatch, processingCaseId, reason, comment, toast),
})

const connector = connect(mapStateToProps, mapDispatchToProps)
type PropsFromRedux = ConnectedProps<typeof connector>

export const TransferDetailsKeyReferenceBar = connector(
  TransferDetailsKeyReferenceBarComponent
)
